import {
	IconBrandGithub,
	IconBrandLinkedin,
	IconBrandTwitter,
} from "@tabler/icons-react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { MarketingHeader } from "@/components/misc/MarketingHeader";
import {
	MarketingContainer,
	MarketingShell,
} from "@/components/misc/marketing-layout";
import { buttonVariants } from "@/components/ui/button";
import { config } from "@/data/config";
import { cn } from "@/lib/utils";
import { MARKETING_SECTION_ANIM_BASE } from "@/utils/animation";
import { seo } from "@/utils/seo";

export const Route = createFileRoute("/_marketing/contact")({
	component: RouteComponent,
	head: () =>
		seo({
			title: "Contact",
			description: `One person behind ${config.siteName}. Questions, abuse, partnerships—you get a human who also ships the code, not a ticket queue.`,
			keywords:
				"contact, support, disposable email, abuse, partnership, help, ineedatest",
		}),
});

export function RouteComponent() {
	const socials = [
		{
			name: "Twitter",
			href: config.Socials.Twitter,
			Icon: IconBrandTwitter,
		},
		{
			name: "GitHub",
			href: config.Socials.Github,
			Icon: IconBrandGithub,
		},
		{
			name: "LinkedIn",
			href: config.Socials.Linkedin,
			Icon: IconBrandLinkedin,
		},
	].filter((s): s is typeof s & { href: string } => Boolean(s.href));

	return (
		<MarketingShell>
			<section className="pb-16 pt-20 sm:pb-24 sm:pt-28">
				<MarketingContainer>
					<div
						className={cn(
							"mx-auto max-w-2xl space-y-10 sm:space-y-12",
							MARKETING_SECTION_ANIM_BASE,
						)}
					>
						<div>
							<MarketingHeader
								align="left"
								title="Solo project"
								heading="Say hello"
								description="No support tiers, no hand-off to 'the team.' If something broke, if you need a takedown, or if you are just curious how this works—you are writing to the same person who runs the servers and reads the abuse queue."
								className="mb-0 sm:mb-0"
								actions={
									<Link
										to="/"
										className={cn(
											buttonVariants({ variant: "outline", size: "default" }),
											"rounded-full px-5",
										)}
									>
										← Back to the inbox
									</Link>
								}
							/>
							<p className="mt-5 max-w-[52ch] text-base leading-7 text-pretty text-muted-foreground sm:text-lg sm:leading-8">
								More about who is behind this:{" "}
								<a
									href={config.projectAttribution.url}
									target="_blank"
									rel="noopener noreferrer"
									className="font-medium text-primary underline-offset-4 transition-colors hover:underline"
								>
									{config.projectAttribution.displayName}
								</a>
								.
							</p>
							<p className="mt-2 max-w-[52ch] text-sm leading-7 text-pretty text-muted-foreground sm:text-base">
								Mail delivery is powered by{" "}
								<a
									href="https://github.com/baker/inboundparse"
									target="_blank"
									rel="noopener noreferrer"
									className="font-medium text-primary underline-offset-4 transition-colors hover:underline"
								>
									InboundParse
								</a>
								.
							</p>
						</div>

						<div className="space-y-4 border-l-2 border-primary/35 pl-5 sm:pl-6">
							<p className="text-base font-medium text-foreground">
								<a
									href={`mailto:${config.supportEmail}`}
									className="text-primary underline-offset-4 transition-colors hover:underline"
								>
									{config.supportEmail}
								</a>
							</p>
							<p className="text-base leading-7 text-pretty text-muted-foreground">
								I read my own inbox. Replies happen when I can—often within a
								couple of days on a good week. Abuse or anything that needs
								legal care can take longer.
							</p>
							<p className="text-base leading-7 text-pretty text-muted-foreground">
								Fully remote; email is the reliable way to reach me across time
								zones.
							</p>
						</div>
					</div>
				</MarketingContainer>
			</section>

			{socials.length > 0 ? (
				<section className="border-t border-border/50 py-14 sm:py-16">
					<MarketingContainer>
						<div
							className={cn(
								"mx-auto max-w-2xl text-center",
								MARKETING_SECTION_ANIM_BASE,
								"delay-100",
							)}
						>
							<h2 className="text-lg font-semibold tracking-tight text-foreground sm:text-xl">
								Elsewhere
							</h2>
							<p className="mx-auto mt-2 max-w-[40ch] text-pretty text-sm leading-6 text-muted-foreground sm:text-base sm:leading-7">
								If you want code or noise between releases, I am on these
								profiles too.
							</p>
							<ul className="mt-8 flex flex-wrap items-center justify-center gap-3">
								{socials.map(({ name, href, Icon }) => (
									<li key={name}>
										<a
											href={href}
											target="_blank"
											rel="noopener noreferrer"
											className={cn(
												buttonVariants({ variant: "outline", size: "default" }),
												"gap-2 rounded-full px-5",
											)}
										>
											<Icon className="size-4 shrink-0" aria-hidden />
											{name}
										</a>
									</li>
								))}
							</ul>
						</div>
					</MarketingContainer>
				</section>
			) : null}
		</MarketingShell>
	);
}
